/**
  * @villus/batch v3.5.1
  * (c) 2024 Abdelrahman Awad
  * @license MIT
  */
import { fetch, definePlugin, CombinedError } from 'villus';
import { print } from 'graphql';

/**
 * Normalizes a query string or object to a string.
 */
function normalizeQuery(query) {
    if (typeof query === 'string') {
        return query;
    }
    // Supports typed document strings in 3.2
    if (query && query instanceof String) {
        return query.toString();
    }
    if (query && 'kind' in query) {
        return print(query);
    }
    return null;
}

async function parseResponse(response) {
    let json;
    const responseData = {
        ok: response.ok,
        statusText: response.statusText,
        status: response.status,
        headers: response.headers,
    };
    try {
        json = await response.json();
    }
    catch (err) {
        return Object.assign(Object.assign({}, responseData), { statusText: err.message, body: null });
    }
    return Object.assign(Object.assign({}, responseData), { body: json });
}
function resolveGlobalFetch() {
    if (typeof window !== 'undefined' && 'fetch' in window && window.fetch) {
        return window.fetch.bind(window);
    }
    if (typeof global !== 'undefined' && 'fetch' in global) {
        return global.fetch;
    }
    if (typeof self !== 'undefined' && 'fetch' in self) {
        return self.fetch;
    }
    return undefined;
}
const DEFAULT_FETCH_OPTS = {
    method: 'POST',
    headers: {
        'content-type': 'application/json',
    },
};
function mergeFetchOpts(lhs, rhs) {
    return Object.assign(Object.assign(Object.assign({}, lhs), rhs), { method: rhs.method || lhs.method || DEFAULT_FETCH_OPTS.method, headers: Object.assign(Object.assign({}, (lhs.headers || {})), (rhs.headers || {})) });
}
function makeFetchOptions({ query, variables }, opts) {
    const normalizedQuery = normalizeQuery(query);
    if (!normalizedQuery) {
        throw new Error('A query must be provided.');
    }
    return mergeFetchOpts({ body: JSON.stringify({ query: normalizedQuery, variables }) }, opts);
}

const defaultOpts = () => ({
    fetch: resolveGlobalFetch(),
    timeout: 10,
    maxOperationCount: 10,
});
function batch(opts) {
    const { fetch: fetch$1, timeout, maxOperationCount } = Object.assign(Object.assign({}, defaultOpts()), (opts || {}));
    const fetchPluginInstance = fetch({ fetch: fetch$1 });
    let operations = [];
    let scheduledConsume;
    return definePlugin(function batchPlugin(ctx) {
        var _a;
        const { useResult, opContext, operation } = ctx;
        if ((_a = opts === null || opts === void 0 ? void 0 : opts.exclude) === null || _a === void 0 ? void 0 : _a.call(opts, ctx.operation, ctx)) {
            return fetchPluginInstance(ctx);
        }
        async function consume() {
            const pending = operations;
            const body = `[${operations.map(o => o.body).join(',')}]`;
            const fetchOpts = mergeFetchOpts(opContext, { headers: {}, body });
            operations = [];
            if (!fetch$1) {
                throw new Error('Could not resolve fetch, please provide a fetch function');
            }
            let response;
            try {
                response = await fetch$1(opContext.url, fetchOpts).then(parseResponse);
                ctx.response = response;
                const resInit = {
                    ok: response.ok,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                };
                pending.forEach(function unBatchResult(o, oIdx) {
                    var _a;
                    const opResult = (_a = response.body) === null || _a === void 0 ? void 0 : _a[oIdx];
                    // the server returned a non-json response or an empty one
                    if (!opResult) {
                        o.resolveOp(Object.assign(Object.assign({}, resInit), { body: response.body }), oIdx, new Error('Received empty response for this operation from server'));
                        return;
                    }
                    o.resolveOp(Object.assign({ body: opResult }, resInit), oIdx);
                });
            }
            catch (err) {
                // This usually mean a network fetch error which is limited to DNS lookup errors
                // or the user may not be connected to the internet, so it's safe to assume no data is in the response
                pending.forEach(function unBatchErrorResult(o, oIdx) {
                    o.resolveOp(undefined, oIdx, err);
                });
            }
        }
        return new Promise(resolve => {
            if (scheduledConsume) {
                clearTimeout(scheduledConsume);
            }
            if (operations.length >= maxOperationCount) {
                // consume the old array
                consume();
            }
            if (!opContext.body) {
                opContext.body = makeFetchOptions(operation, opContext).body;
            }
            operations.push({
                resolveOp: (response, opIdx, err) => {
                    var _a;
                    resolve(undefined);
                    // Handle DNS errors
                    if (err) {
                        useResult({
                            data: null,
                            error: new CombinedError({
                                response,
                                networkError: err,
                            }),
                        }, true);
                        return;
                    }
                    const data = ((_a = response.body) === null || _a === void 0 ? void 0 : _a.data) || null;
                    if (!response.ok || !response.body) {
                        const error = buildErrorObject(response, opIdx);
                        useResult({
                            data,
                            error,
                        }, true);
                        return;
                    }
                    useResult({
                        data,
                        error: response.body.errors ? new CombinedError({ response, graphqlErrors: response.body.errors }) : null,
                    }, true);
                },
                body: opContext.body,
            });
            scheduledConsume = setTimeout(consume, timeout);
        });
    });
}
function buildErrorObject(response, opIdx) {
    var _a;
    // It is possible than a non-200 response is returned with errors, it should be treated as GraphQL error
    const ctorOptions = {
        response,
    };
    if (Array.isArray(response.body)) {
        const opResponse = response.body[opIdx];
        ctorOptions.graphqlErrors = opResponse === null || opResponse === void 0 ? void 0 : opResponse.errors;
    }
    else if ((_a = response.body) === null || _a === void 0 ? void 0 : _a.errors) {
        ctorOptions.graphqlErrors = response.body.errors;
    }
    else {
        ctorOptions.networkError = new Error(response.statusText);
    }
    return new CombinedError(ctorOptions);
}

export { batch };
